import React, {useEffect, useRef, useState} from 'react';
import {BrainCircuit, Radio, ShieldCheck, SlidersHorizontal, Waypoints, Zap} from 'lucide-react';
import type {FlowStepContent} from '../../pages/site/site.types';
import styles from './styles/CapabilityFlow.module.css';

const autoIcons = [
    <SlidersHorizontal size={22}/>,
    <Radio size={22}/>,
    <BrainCircuit size={22}/>,
    <ShieldCheck size={22}/>,
    <Zap size={22}/>,
    <Waypoints size={22}/>
];

interface CardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    tags: string[];
    index: number;
    transitionDelay: number;
}

const LayerCard: React.FC<CardProps> = ({icon, title, description, tags, index, transitionDelay}) => (
    <article className={styles.card} style={{transitionDelay: `${transitionDelay}ms`}}>
        <div className={styles.cardIcon}>
            {icon}
        </div>
        <div className={styles.cardBody}>
            <div className={styles.cardHead}>
                <span className={styles.index}>0{index + 1}</span>
                <h3>{title}</h3>
            </div>
            <p className={styles.cardDesc}>{description}</p>
            <div className={styles.tags}>
                {tags.map(tag => (
                    <span className="site-chip" key={tag}>{tag}</span>
                ))}
            </div>
        </div>
    </article>
);

interface CapabilityFlowProps {
    steps: FlowStepContent[];
    feedbackLoop?: boolean;
}

const CapabilityFlow: React.FC<CapabilityFlowProps> = ({steps, feedbackLoop = true}) => {
    const ref = useRef<HTMLDivElement>(null);
    const sixGridRef = useRef<HTMLDivElement>(null);
    const card2Ref = useRef<HTMLDivElement>(null);
    const card6Ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    const [feedbackLoopPath, setFeedbackLoopPath] = useState('');

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            {threshold: 0.15}
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (steps.length !== 6 || !feedbackLoop) {
            setFeedbackLoopPath('');
            return;
        }

        const updateFeedbackLoopPath = () => {
            const gridEl = sixGridRef.current;
            const card2El = card2Ref.current;
            const card6El = card6Ref.current;
            if (!gridEl || !card2El || !card6El || window.innerWidth <= 640) {
                setFeedbackLoopPath('');
                return;
            }

            const gridRect = gridEl.getBoundingClientRect();
            const card2Rect = card2El.getBoundingClientRect();
            const card6Rect = card6El.getBoundingClientRect();

            const startX = card6Rect.left - gridRect.left + card6Rect.width * 0.68;
            const startY = card6Rect.top - gridRect.top + 18;
            const endX = card2Rect.left - gridRect.left + card2Rect.width * 0.22;
            const endY = card2Rect.bottom - gridRect.top - 18;
            const laneY = (card6Rect.top + card2Rect.bottom) / 2 - gridRect.top;
            const midX = startX + (endX - startX) * 0.52;
            const controlOffset = Math.max(24, (endX - startX) * 0.18);

            setFeedbackLoopPath(
                [
                    `M ${startX} ${startY}`,
                    `C ${startX} ${laneY}, ${midX - controlOffset} ${laneY}, ${midX} ${laneY}`,
                    `S ${endX} ${laneY - 10}, ${endX} ${endY}`
                ].join(' ')
            );
        };

        updateFeedbackLoopPath();

        const resizeObserver = new ResizeObserver(() => updateFeedbackLoopPath());
        if (sixGridRef.current) {
            resizeObserver.observe(sixGridRef.current);
        }
        if (card2Ref.current) {
            resizeObserver.observe(card2Ref.current);
        }
        if (card6Ref.current) {
            resizeObserver.observe(card6Ref.current);
        }
        window.addEventListener('resize', updateFeedbackLoopPath);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateFeedbackLoopPath);
        };
    }, [feedbackLoop, steps.length]);

    if (steps.length === 6) {
        const layers = steps.map((step, index) => ({
            icon: autoIcons[index],
            title: step.title,
            description: step.description,
            tags: step.tags
        }));
        const [c1, c2, c3, c4, c5, c6] = layers;

        return (
            <div ref={ref} className={`${styles.flow} ${visible ? styles.animate : ''}`}>
                <div ref={sixGridRef} className={`${styles.grid} ${styles.gridSix}`}>
                    {feedbackLoopPath ? (
                        <svg
                            aria-hidden="true"
                            className={`${styles.feedbackLoop} ${styles.connAnimate}`}
                        >
                            <path className={styles.feedbackLoopGlow} d={feedbackLoopPath}/>
                            <path className={styles.feedbackLoopPath} d={feedbackLoopPath}/>
                            <circle r="4" className={styles.feedbackDot}>
                                <animateMotion dur="2s" repeatCount="indefinite" path={feedbackLoopPath}/>
                            </circle>
                        </svg>
                    ) : null}
                    <div className={styles.card1}>
                        <LayerCard icon={c1.icon} title={c1.title} description={c1.description} tags={c1.tags} index={0}
                                   transitionDelay={0}/>
                    </div>
                    <div className={styles.connH12}>
                        <div className={`${styles.connLineH} ${styles.connAnimate}`} style={{transitionDelay: '150ms'}}>
                            <div className={styles.dotR}/>
                        </div>
                    </div>
                    <div ref={card2Ref} className={styles.card2}>
                        <LayerCard icon={c2.icon} title={c2.title} description={c2.description} tags={c2.tags} index={1}
                                   transitionDelay={120}/>
                    </div>
                    <div className={styles.connH23}>
                        <div className={`${styles.connLineH} ${styles.connAnimate}`} style={{transitionDelay: '300ms'}}>
                            <div className={styles.dotR}/>
                        </div>
                    </div>
                    <div className={styles.card3}>
                        <LayerCard icon={c3.icon} title={c3.title} description={c3.description} tags={c3.tags} index={2}
                                   transitionDelay={240}/>
                    </div>

                    <div className={styles.connV34}>
                        <div className={`${styles.connLineV} ${styles.connAnimate}`} style={{transitionDelay: '450ms'}}>
                            <div className={styles.dotD}/>
                        </div>
                    </div>

                    <div className={styles.card4}>
                        <LayerCard icon={c4.icon} title={c4.title} description={c4.description} tags={c4.tags} index={3}
                                   transitionDelay={480}/>
                    </div>
                    <div className={styles.connH54}>
                        <div className={`${styles.connLineHReverse} ${styles.connAnimate}`}
                             style={{transitionDelay: '600ms'}}>
                            <div className={styles.dotL}/>
                        </div>
                    </div>
                    <div className={styles.card5}>
                        <LayerCard icon={c5.icon} title={c5.title} description={c5.description} tags={c5.tags} index={4}
                                   transitionDelay={600}/>
                    </div>
                    <div className={styles.connH65}>
                        <div className={`${styles.connLineHReverse} ${styles.connAnimate}`}
                             style={{transitionDelay: '750ms'}}>
                            <div className={styles.dotL}/>
                        </div>
                    </div>
                    <div ref={card6Ref} className={styles.card6}>
                        <LayerCard icon={c6.icon} title={c6.title} description={c6.description} tags={c6.tags} index={5}
                                   transitionDelay={720}/>
                    </div>
                </div>
            </div>
        );
    }

    const layers = steps.map((step, index) => ({
        icon: autoIcons[index % autoIcons.length],
        title: step.title,
        description: step.description,
        tags: step.tags
    }));
    const [c1, c2, c3, c4] = layers;

    return (
        <div ref={ref} className={`${styles.flow} ${visible ? styles.animate : ''}`}>
            <div className={styles.grid}>
                <div className={styles.card1}>
                    <LayerCard icon={c1.icon} title={c1.title} description={c1.description} tags={c1.tags} index={0}
                               transitionDelay={0}/>
                </div>
                <div className={styles.connH12}>
                    <div className={`${styles.connLineH} ${styles.connAnimate}`} style={{transitionDelay: '200ms'}}>
                        <div className={styles.dotR}/>
                    </div>
                </div>
                <div className={styles.card2}>
                    <LayerCard icon={c2.icon} title={c2.title} description={c2.description} tags={c2.tags} index={1}
                               transitionDelay={150}/>
                </div>

                <div className={styles.spacerLeft}/>
                <div className={styles.spacerMid}/>
                <div className={styles.connV23}>
                    <div className={`${styles.connLineV} ${styles.connAnimate}`} style={{transitionDelay: '450ms'}}>
                        <div className={styles.dotD}/>
                    </div>
                </div>

                <div className={styles.card4}>
                    <LayerCard icon={c4.icon} title={c4.title} description={c4.description} tags={c4.tags} index={3}
                               transitionDelay={450}/>
                </div>
                <div className={styles.connH43}>
                    <div className={`${styles.connLineHReverse} ${styles.connAnimate}`}
                         style={{transitionDelay: '700ms'}}>
                        <div className={styles.dotL}/>
                    </div>
                </div>
                <div className={styles.card3}>
                    <LayerCard icon={c3.icon} title={c3.title} description={c3.description} tags={c3.tags} index={2}
                               transitionDelay={300}/>
                </div>
            </div>
        </div>
    );
};

export default CapabilityFlow;
